import { ClipboardModule } from '@angular/cdk/clipboard'
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatDialogModule } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { LIB_LOGIN_CONFIG } from '../../../provider'
import { SnackbarService } from '../../../services'
import { CopyButtonComponent } from '../../buttons/copy-button/copy-button.component'

/**
 * Was jemand tun muss, der noch keinen Zugang hat.
 *
 * <p>**Warum es den Dialog gibt:** die Auth0-Connection hat „Disable Sign Ups" gesetzt, und das ist
 * so gewollt — Zugaenge legt das Sanitaetshaus in der Benutzerverwaltung an, mit Rolle und Passwort
 * in einem Schritt ({@code PortalNutzerDialogComponent}). Wer trotzdem auf „Registrieren" ging,
 * landete bei Auth0 und kam mit der englischen Zeile „signup is disabled" als roter Fehlermeldung
 * zurueck (Befund 2026-09-04). Fachlich richtig, praktisch eine Sackgasse: der Satz sagt weder, wer
 * einen Zugang anlegt, noch wo.
 *
 * <p>Der Dialog nimmt beides ab und liefert dazu die fertige Nachricht zum Weiterschicken — dasselbe
 * Muster wie der Bildschirm fuer ein Konto ohne Rolle ({@code ZugangAusstehendComponent} in der
 * Leistungserbringer-App). Kopieren statt {@code mailto:}: ein Mailprogramm ist am Arbeitsplatz
 * eines Sanitaetshauses oft gar nicht eingerichtet, und dann passiert beim Klick sichtbar nichts
 * (Urteil Tim 2026-08-23).
 *
 * <p>Die eigene Adresse steht hier als Eingabefeld, nicht als gelesener Wert: auf der Anmeldeseite
 * ist niemand angemeldet, es gibt also kein Konto, aus dem sie kommen koennte. Sie ist freiwillig —
 * ohne sie traegt die Nachricht an der Stelle einen Platzhalter.
 */
@Component({
  selector: 'lib-zugang-anfordern-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, ClipboardModule, CopyButtonComponent],
  templateUrl: './zugang-anfordern-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './zugang-anfordern-dialog.component.scss',
})
export class ZugangAnfordernDialogComponent {
  private readonly snackbarService = inject(SnackbarService)
  private readonly loginConfig = inject(LIB_LOGIN_CONFIG, { optional: true })

  readonly eigeneEmail = signal('')

  /**
   * Absolut ueber {@code location.origin}, weil der Link zum Verschicken gedacht ist und in jeder
   * Stage stimmen muss. Ohne den Pfad in der {@code LoginConfig} oeffnet {@code LoginComponent}
   * den Dialog gar nicht (interne Verwaltung: dort registriert sich niemand, Entscheidung Tim
   * 2026-10-06). Der Fallback auf leer ist nur fuer den Typ da.
   */
  readonly benutzerverwaltungLink = `${window.location.origin}${this.loginConfig?.benutzerverwaltungPfad ?? ''}`

  /** Fertige Nachricht zum Weiterschicken. Ein Klick statt selbst formulieren. */
  readonly nachricht = computed(() => {
    const adresse = this.eigeneEmail().trim() || '(hier meine E-Mail-Adresse eintragen)'
    return [
      'Hallo,',
      '',
      'ich möchte SaniUp ONE nutzen und brauche einen Zugang. Selbst registrieren kann ich mich nicht.',
      '',
      'Kannst du mir bitte einen Zugang anlegen?',
      '1. In SaniUp ONE oben rechts auf das Zahnrad klicken.',
      '2. Den Reiter "Benutzer:innen" öffnen.',
      '3. Auf "Zugang anlegen" klicken.',
      '',
      `Benutzerverwaltung: ${this.benutzerverwaltungLink}`,
      `Meine E-Mail-Adresse: ${adresse}`,
      '',
      'Das Passwort vergibst du dabei selbst. Bitte gib es mir danach weiter, ich bekomme dazu keine E-Mail.',
      '',
      'Danke!',
    ].join('\n')
  })

  emailEingeben(event: Event) {
    this.eigeneEmail.set((event.target as HTMLInputElement).value)
  }

  nachrichtKopiert(erfolgreich: boolean) {
    if (erfolgreich) {
      this.snackbarService.showSuccess('Nachricht kopiert')
    } else {
      this.snackbarService.showError('Fehler beim Kopieren')
    }
  }
}
